import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { C, R, TESTIMONIALS } from "../../data/content";
import { Portrait } from "../HostSection";

gsap.registerPlugin(ScrollTrigger);

const goBook = () => document.getElementById("book")?.scrollIntoView({ behavior: "smooth" });

export const OfferStack = () => {
  const ref = useRef(null);
  useEffect(() => {
    gsap.fromTo(ref.current.querySelectorAll(".stack-item"), { opacity: 0, x: -20 }, {
      opacity: 1, x: 0, duration: 0.5, stagger: 0.12, ease: "power2.out",
      scrollTrigger: { trigger: ref.current, start: "top 75%" },
    });
  }, []);
  return (
    <section ref={ref} className="py-28 px-6 lg:px-12" data-testid="offer-stack-section">
      <div className="max-w-[820px] mx-auto">
        <p className="mono text-[11px] tracking-[0.25em] text-[var(--accent)] mb-6">[002] — THE OFFER STACK</p>
        <h2 className="h1-display text-[clamp(28px,3.4vw,48px)] mb-12">HERE'S WHAT'S WAITING WHEN YOU BOOK</h2>
        <div className="glass-card p-8 md:p-10">
          <ul className="space-y-5">
            {C.bonuses.map((b, i) => (
              <li key={i} className="stack-item flex gap-4 items-start" data-testid={`stack-item-${i + 1}`}>
                <span className="text-[var(--accent)] font-black text-lg shrink-0">✓</span>
                <p className="text-[15px] text-white/75 font-medium">{b}</p>
              </li>
            ))}
          </ul>
          <div className="stack-item mt-8 pt-7 border-t border-white/10">
            <p className="text-[17px] font-black">Your investment today: <span className="text-[var(--accent)]">$0</span> — just 45 minutes and your honesty.</p>
          </div>
        </div>
        <button className="btn-accent cta mt-10" onClick={goBook} data-testid="stack-cta">{R.cta}</button>
      </div>
    </section>
  );
};

export const Guarantee = () => (
  <section className="py-28 px-6" data-testid="guarantee-section">
    <div className="max-w-[720px] mx-auto text-center">
      <p className="mono text-[11px] tracking-[0.25em] text-[var(--accent)] mb-10">[003] — THE GUARANTEE</p>
      <div className="border p-10 md:p-14" style={{ borderColor: "var(--accent)", background: "rgba(68,76,231,0.05)" }}>
        <h2 className="h1-display text-[clamp(24px,2.8vw,38px)] mb-6">{R.guaranteeTitle}</h2>
        <p className="text-[15px] text-white/60 leading-[1.8]">{R.guaranteeBody}</p>
      </div>
      <p className="text-[11px] text-white/30 mt-5">{R.guaranteeSmall}</p>
    </div>
  </section>
);

const creds = [
  ["COMPANY", "NEXA Mortgage"],
  ["NMLS#", "122481"],
  ["CO. NMLS#", "1660690 (NEXA Mortgage LLC)"],
  ["LICENSED IN", "FL, TX, PA"],
];

export const ReplayHost = () => (
  <section id="host" className="py-28 px-6 lg:px-12" data-testid="replay-host-section">
    <div className="max-w-[1200px] mx-auto">
      <p className="mono text-[11px] tracking-[0.25em] text-[var(--accent)] mb-12">[004] — YOUR HOST</p>
      <div className="grid md:grid-cols-[minmax(280px,420px)_1fr] gap-14 items-start">
        <Portrait />
        <div>
          <h2 className="h1-display text-[clamp(26px,3vw,42px)] mb-6">{R.hostH2}</h2>
          <div className="h-px w-24 mb-8" style={{ background: "var(--accent)" }} />
          <div className="space-y-5 text-[15px] text-white/55 leading-[1.8]">
            {R.hostParas.map((p, i) => <p key={i}>{p}</p>)}
          </div>
          <div className="grid grid-cols-2 gap-px mt-10 border border-white/[0.08] bg-white/[0.08]">
            {creds.map(([k, v]) => (
              <div key={k} className="bg-[#080808] p-5">
                <p className="mono text-[10px] tracking-[0.25em] text-white/35 mb-2">{k}</p>
                <p className="text-[13px] font-bold">{v}</p>
              </div>
            ))}
          </div>
          <button className="btn-accent cta mt-10" onClick={goBook} data-testid="replay-host-cta">{R.cta}</button>
        </div>
      </div>
    </div>
  </section>
);

export const Objections = () => (
  <section id="method" className="py-28 px-6 lg:px-12" style={{ background: "rgba(255,255,255,0.015)" }} data-testid="objections-section">
    <div className="max-w-[1200px] mx-auto grid md:grid-cols-2 gap-16">
      <div>
        <p className="mono text-[11px] tracking-[0.25em] text-[var(--accent)] mb-6">[005] — STILL DECIDING?</p>
        <h2 className="h1-display text-[clamp(26px,3vw,42px)] mb-8" style={{ textTransform: "none", letterSpacing: "-0.02em" }}>{R.objectionsHeading}</h2>
        <div className="space-y-5 text-[15px] text-white/55 leading-[1.8]">
          {R.objectionParas.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </div>
      <div className="md:pt-20">
        <p className="mono text-[12px] tracking-[0.2em] text-[var(--accent)] mb-8">HERE'S WHAT HAPPENS WHEN YOU BOOK:</p>
        <ul className="space-y-6">
          {R.bookBullets.map((t, i) => (
            <li key={i} className="flex gap-4 items-start" data-testid={`book-bullet-${i + 1}`}>
              <span className="text-[var(--accent)] font-black text-lg">→</span>
              <p className="text-[15px] text-white/75 font-medium leading-relaxed">{t}</p>
            </li>
          ))}
        </ul>
        <button className="btn-accent cta mt-12" onClick={goBook} data-testid="objections-cta">{R.cta}</button>
      </div>
    </div>
  </section>
);

export const ReplayProof = () => (
  <section className="py-28 px-6 lg:px-12" data-testid="replay-proof-section">
    <div className="max-w-[1200px] mx-auto">
      <p className="mono text-[11px] tracking-[0.25em] text-[var(--accent)] mb-6">[006] — PROOF</p>
      <h2 className="h1-display text-[clamp(28px,3.4vw,48px)] mb-12">REAL VETERANS, REAL CLOSINGS</h2>
      <div className="grid md:grid-cols-3 gap-4">
        {TESTIMONIALS.map((t, i) => (
          <div key={i} className="glass-card p-7 flex flex-col gap-4" data-testid={`replay-testimonial-${i + 1}`}>
            <div>
              <div className="flex items-center justify-between">
                <p className="font-black text-[15px]">{t.name}</p>
                <p className="text-[var(--accent)] tracking-[0.15em] text-sm">★★★★★</p>
              </div>
              <p className="mono text-[10px] text-white/30 mt-1">{t.date}</p>
            </div>
            <p className="text-[13px] text-white/55 leading-relaxed italic">"{t.text}"</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const BOOKING_URL = "https://link.smallbusinessautomator.com/widget/booking/0ek5j0z2c1R9ppwZy214";

const BookingWidget = () => {
  useEffect(() => {
    if (!document.querySelector('script[src="https://link.msgsndr.com/js/form_embed.js"]')) {
      const s = document.createElement("script");
      s.src = "https://link.msgsndr.com/js/form_embed.js";
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);
  return (
    <div className="glass-card p-2 md:p-3" data-testid="booking-widget-card">
      <iframe
        src={BOOKING_URL}
        title="Book Your Veteran Fast-Track Review"
        id="0ek5j0z2c1R9ppwZy214_booking"
        className="w-full rounded"
        style={{ border: "none", overflow: "hidden", minHeight: "760px", background: "transparent" }}
        scrolling="no"
        data-testid="booking-widget-iframe"
      />
    </div>
  );
};

export const FinalBooking = () => (
  <section id="book" className="relative py-32 px-6 lg:px-12 grid-bg overflow-hidden" style={{ background: "rgba(255,255,255,0.012)" }} data-testid="final-booking-section">
    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(68,76,231,0.16), transparent 65%)" }} />
    <div className="relative max-w-[1200px] mx-auto grid lg:grid-cols-2 gap-16 items-center">
      <div>
        <p className="mono text-[11px] tracking-[0.25em] text-[var(--accent)] mb-8">[008] — BOOK YOUR REVIEW</p>
        <h2 className="h1-display text-[clamp(34px,4.2vw,60px)]">
          KNOW YOUR NUMBER. MAKE YOUR OFFER. <span className="text-[var(--accent)]">CLOSE ON TIME.</span>
        </h2>
        <ul className="mt-10 space-y-4">
          {R.bookTrust.map((t, i) => (
            <li key={i} className="flex gap-3 items-start text-[14px] text-white/60" data-testid={`booking-trust-${i + 1}`}>
              <span className="text-[var(--accent)] font-black">✓</span>{t}
            </li>
          ))}
        </ul>
      </div>
      <div className="w-full lg:ml-auto">
        <BookingWidget />
      </div>
    </div>
  </section>
);
